// eslint-disable-next-line import/no-cycle
import { adminApi } from '../includes/contentManagementApi';

/**
 * Scheduler Service
 * Handles all API calls related to the DevTips scheduler
 */
export default {
  /**
   * Get current scheduler status and health information
   * @returns {Promise} Scheduler status response
   */
  getStatus() {
    return adminApi.get('/scheduler/status');
  },

  /**
   * Get scheduler health check
   * @returns {Promise} Health check response
   */
  getHealth() {
    return adminApi.get('/scheduler/health');
  },

  /**
   * Trigger DevTips scheduling for all periods
   * @returns {Promise} Trigger response
   */
  triggerDevTipsScheduling() {
    return adminApi.post('/scheduler/trigger/dev-tips');
  },

  /**
   * Trigger daily scheduling
   * @returns {Promise} Daily trigger response
   */
  triggerDailyScheduling() {
    return adminApi.post('/scheduler/trigger/daily');
  },

  /**
   * Trigger weekly scheduling
   * @returns {Promise} Weekly trigger response
   */
  triggerWeeklyScheduling() {
    return adminApi.post('/scheduler/trigger/weekly');
  },

  /**
   * Trigger monthly scheduling
   * @returns {Promise} Monthly trigger response
   */
  triggerMonthlyScheduling() {
    return adminApi.post('/scheduler/trigger/monthly');
  },

  /**
   * Trigger yearly scheduling
   * @returns {Promise} Yearly trigger response
   */
  triggerYearlyScheduling() {
    return adminApi.post('/scheduler/trigger/yearly');
  },

  /**
   * Trigger level-system inactivity detection (sends warnings to inactive users)
   * @returns {Promise} Trigger response
   */
  triggerInactivityDetection() {
    return adminApi.post('/scheduler/trigger/inactivity-detection');
  },

  /**
   * Trigger travel buddy matching for the current tenant
   * @returns {Promise} Trigger response
   */
  triggerTravelBuddyMatching() {
    return adminApi.post('/scheduler/trigger/travel-buddy');
  },

  /**
   * Travel Buddy Simulator: complete up to 10 random 'created' meetings, run matching, return new meetings with names and category
   * @returns {Promise} { completedCount, newMeetings }
   */
  simulateTravelBuddy() {
    return adminApi.post('/scheduler/simulate-travel-buddy');
  },

  /**
   * In-memory simulation (no DB): get current tenant state for travel buddy simulator
   * @returns {Promise} { users, workshops, configRound, eligiblePoolIds }
   */
  getTravelBuddySimulationState() {
    return adminApi.get('/scheduler/travel-buddy-simulation-state');
  },

  /**
   * In-memory simulation (no DB): run until every pair has at least one completed meeting
   * @param {number} skipProbability - 0–1, probability that a user skips a meeting
   * @returns {Promise} { allMatchedPairs, rounds, summary }
   */
  runTravelBuddySimulation(skipProbability = 0.25) {
    return adminApi.post('/scheduler/travel-buddy-simulation-run', { skipProbability });
  },

  /**
   * Trigger anomaly report (Level System + Travel Buddy) for all tenants
   * @returns {Promise} Trigger response
   */
  triggerAnomalyReport() {
    return adminApi.post('/scheduler/trigger/anomaly-report');
  },

  /**
   * Trigger a specific job type
   * @param {string} jobType - The type of job to trigger
   * @returns {Promise} Job trigger response
   */
  triggerJob(jobType) {
    switch (jobType) {
      case 'daily':
        return this.triggerDailyScheduling();
      case 'weekly':
        return this.triggerWeeklyScheduling();
      case 'monthly':
        return this.triggerMonthlyScheduling();
      case 'yearly':
        return this.triggerYearlyScheduling();
      case 'dev-tips':
        return this.triggerDevTipsScheduling();
      default:
        throw new Error(`Unknown job type: ${jobType}`);
    }
  },
};
